import React, {useState} from 'react';

import CSSModules from 'react-css-modules';

import {getVideoUrl, getYouTubeIdFromUrl} from '@edume/bento/videoUtil.browser';

import {createMarkup, renderMarkdown} from '../../utils/markdownUtils';
import {VideoContent, YouTubeContent} from '../../Video';

import styles from './styles.module.scss';

type VideoProps = {
  editMode?: boolean;
  url: string;
  source?: string;
  caption?: string;
  captionPlaceholder?: string;
  retryText: string;
  autoPlay?: boolean;
  onPlay?: () => void;
  onFinish?: () => void;
  onToggleFullscreen?: () => void;
  reskinClassName?: string;
  isSkippable?: boolean;
};

// eslint-disable-next-line complexity
const Video = ({
  url,
  source,
  caption,
  captionPlaceholder,
  retryText,
  editMode = false,
  autoPlay,
  onPlay,
  onFinish,
  onToggleFullscreen: onToggleFs,
  reskinClassName = '',
  isSkippable = false,
}: VideoProps): JSX.Element | null => {
  const [isIosFullscreen, setIsIosFullscreen] = useState(false);

  if (source && !['cloudinary', 'youtube'].includes(source) && !editMode) {
    return null;
  }

  let videoContent;

  const onToggleFullscreen = () => {
    setIsIosFullscreen((prev) => !prev);
    if (onToggleFs) {
      onToggleFs();
    }
  };

  if (source === 'cloudinary') {
    videoContent = (
      <VideoContent
        url={getVideoUrl(url)}
        isSkippable={isSkippable}
        retryText={retryText}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onFinish={onFinish}
        videoStyle={isIosFullscreen ? {} : {borderRadius: '13px 13px 0 0'}}
        onToggleFullscreen={onToggleFullscreen}
      />
    );
  } else if (source === 'youtube') {
    videoContent = (
      <YouTubeContent
        autoPlay={autoPlay}
        onPlay={onPlay}
        onFinish={onFinish}
        youtubeId={getYouTubeIdFromUrl(url)}
      />
    );
  }

  if (editMode && !url) {
    videoContent = <div styleName='placeholder-thumbnail' />;
  }

  const showCaption = caption || captionPlaceholder;

  let styleName = 'video-content';
  if (!showCaption) {
    styleName += ' no-caption';
  }
  if (source === 'youtube') {
    styleName += ' youtube';
  }
  if (editMode) {
    styleName += ' editing';
  }
  if (isIosFullscreen) {
    styleName += ' fullscreen';
  }

  return (
    <div styleName={reskinClassName}>
      <div styleName={styleName} data-automation='component-video'>
        {videoContent}
      </div>
      {showCaption && (
        <div
          styleName={`caption ${!caption ? 'grey' : ''}`}
          data-automation='component-description'
        >
          <div
            dangerouslySetInnerHTML={createMarkup(
              renderMarkdown(caption || captionPlaceholder || '')
            )}
          />
        </div>
      )}
    </div>
  );
};

export default CSSModules(Video, styles, {allowMultiple: true});
